import { ChevronDownIcon, MenuIcon, PlusIcon, SearchIcon, Star, Sun, User } from 'lucide-react'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { useAuth } from '@/services/authentication.service'

interface SectionHeaderProps {
    title: string
    subtitle?: string
    className?: string
    variant?: 'default' | 'compact' | 'chat' | 'hero'

    showDate?: boolean
    dateIcon?: React.ReactNode

    isMobile?: boolean
    onSidebarToggle?: () => void
    onNewChat?: () => void

    progressBar?: ReactNode
    children?: ReactNode
}

export function SectionHeader({
    title,
    subtitle,
    className = '',
    variant = 'default',

    showDate = false,
    dateIcon,

    isMobile,
    onSidebarToggle,
    onNewChat,

    progressBar,
    children,
}: SectionHeaderProps) {
    const isCompact = variant === 'compact'
    const isChat = variant === 'chat'
    const isHero = variant === 'hero'
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()
    const { user } = useAuth()

    const navLinks = [
        { text: 'Providers', href: '/providers', icon: SearchIcon },
        { text: 'Profile', href: '/profile', icon: User },
    ]

    const currentDate = new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    })

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    if (isChat) {
        return (
            <>
                <header
                    className={`relative overflow-hidden bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500 ${className}`}
                >
                    <div className='absolute inset-0 overflow-hidden'>
                        <div className='absolute -top-24 -right-24 w-96 h-96 rounded-full bg-secondary-300/20 blur-3xl' />
                        <div className='absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-brand-300/30 blur-3xl' />
                        <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-white/5 blur-3xl' />
                    </div>

                    <div className='relative flex items-center justify-between px-4 py-3'>
                        <div className='flex items-center gap-3'>
                            <button
                                onClick={onSidebarToggle}
                                className='p-2 text-white transition-all duration-200 rounded-lg hover:bg-white/15'
                                title='Open sidebar'
                            >
                                <MenuIcon className='w-5 h-5' />
                            </button>
                            <div>
                                <h1 className='max-w-md text-sm font-medium text-white truncate'>{title}</h1>
                                {subtitle && <p className='text-xs text-brand-100 truncate'>{subtitle}</p>}
                            </div>
                        </div>

                        <div className='flex items-center gap-2'>
                            <button
                                onClick={onNewChat}
                                className='p-2 text-white transition-colors rounded-lg hover:bg-white/15'
                                title='New Chat'
                            >
                                <PlusIcon className='w-4 h-4' />
                            </button>
                        </div>
                    </div>
                </header>
            </>
        )
    }

    return (
        <header
            className={`relative overflow-hidden bg-gradient-to-r from-brand-800 via-brand-600 to-brand-500 ${className}`}
        >
            <div className='absolute inset-0 overflow-hidden'>
                <div className='absolute -top-24 -right-24 w-96 h-96 rounded-full bg-secondary-300/20 blur-3xl' />
                <div className='absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-brand-300/30 blur-3xl' />
                <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-white/5 blur-3xl' />
            </div>

            <div className='relative px-4 sm:px-6 lg:px-8 py-6 sm:py-10'>
                <div className='mx-auto max-w-5xl'>
                    <div className='flex items-start justify-between'>
                        <div>
                            {showDate && (
                                <div className='flex items-center gap-2 mb-3'>
                                    <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-100/10 backdrop-blur-sm text-white/90 text-sm font-medium'>
                                        {dateIcon || <Sun className='w-5 h-5 text-secondary-300' />}
                                        {currentDate}
                                    </span>
                                </div>
                            )}

                            <h1 className='text-2xl sm:text-2xl lg:text-3xl font-bold text-white mb-2'>{title}</h1>
                            {subtitle && <p className='text-brand-100 text-sm sm:text-lg max-w-xl'>{subtitle}</p>}
                        </div>
                    </div>
                </div>
            </div>
            <div className='absolute bottom-0 left-0 right-0'>
                <div className='absolute bottom-0 left-0 right-0 h-1 bg-white'></div>

                <svg
                    viewBox='0 0 1440 48'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    className='w-full h-auto relative z-10 translate-y-[1px]'
                    preserveAspectRatio='none'
                >
                    <path
                        d='M0 48V24C180 36 360 12 540 24C720 36 900 12 1080 24C1260 36 1440 24 1440 24V48H0Z'
                        className='fill-white/50'
                    />
                    <path
                        d='M0 48V32C180 42 360 22 540 32C720 42 900 22 1080 32C1260 42 1440 32 1440 32V48H0Z'
                        className='fill-white'
                    />

                    <rect x='0' y='47' width='1440' height='5' className='fill-white' />
                </svg>
            </div>

            {children}
        </header>
    )
}
